import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Button, Label, Input, Textarea, Select, SelectItem, DateInput, MultiSelect } from '../../../components/ui';
import { useFlexibleList, useEntity } from '../../../hooks/useSWR';
import { PlusIcon, TrashIcon, ChevronDownIcon } from '../../../components/ui/Icons';

const ChevronUpIcon = ({ className = 'h-5 w-5', ...props }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
  </svg>
);

const ChevronRightIcon = ({ className = 'h-5 w-5', ...props }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

/**
 * Flatten API response: each finding has capaActions[] -> one row = finding + capaActions[0]
 */
function flattenFindingsForForm(findings) {
  if (!findings || !Array.isArray(findings)) return [];
  return findings.map((f) => {
    const action = f.capaActions && f.capaActions[0] ? f.capaActions[0] : {};
    return {
      id: f.id,
      code: f.code ?? '',
      description: f.description ?? '',
      criticalityId: f.criticalityId ?? null,
      criticality: f.criticality,
      gxpReference: f.gxpReference ?? '',
      rootCauseCategoryId: f.rootCauseCategoryId ?? null,
      rootCauseCategory: f.rootCauseCategory,
      rootCauseDetails: f.rootCauseDetails ?? '',
      actionDescription: action.actionDescription ?? '',
      responsiblePersons: action.responsiblePersons ?? '',
      responsibleUserIds: Array.isArray(action.responsibleUserIds) ? action.responsibleUserIds : [],
      dueDate: action.dueDate ?? null,
      completionDate: action.completionDate ?? null,
      extensionRequested: action.extensionRequested ?? false,
      extensionReason: action.extensionReason ?? '',
      newDueDate: action.newDueDate ?? null,
      newCompletionDate: action.newCompletionDate ?? null,
      statusId: action.statusId ?? null,
      status: action.status,
    };
  });
}

/**
 * Convert form rows to API payload (finding + one capa action per row)
 */
function findingsToPayload(rows) {
  return (rows || []).map((row) => ({
    code: row.code ?? '',
    description: row.description ?? '',
    criticalityId: row.criticalityId || null,
    gxpReference: row.gxpReference || null,
    rootCauseCategoryId: row.rootCauseCategoryId || null,
    rootCauseDetails: row.rootCauseDetails || null,
    actionDescription: row.actionDescription ?? '',
    responsiblePersons: row.responsiblePersons || null,
    responsibleUserIds: Array.isArray(row.responsibleUserIds) ? row.responsibleUserIds : [],
    dueDate: row.dueDate || null,
    completionDate: row.completionDate || null,
    extensionRequested: row.extensionRequested ?? false,
    extensionReason: row.extensionReason || null,
    newDueDate: row.newDueDate || null,
    newCompletionDate: row.newCompletionDate || null,
    statusId: row.statusId || null,
  }));
}

const ExternalAuditFindingsTable = ({
  externalAuditId,
  formData,
  setFormData,
  config,
  isEditMode = false,
  selectedRecord = null,
}) => {
  const { theme } = useTheme();
  const [isExpanded, setIsExpanded] = useState(true);
  const [localRows, setLocalRows] = useState([]);
  const [expandedRowIndex, setExpandedRowIndex] = useState(new Set([0]));
  const isInitializedRef = useRef(false);
  const localRowsRef = useRef([]);
  const recordId = selectedRecord?.id ?? externalAuditId ?? formData?.id;

  const usersEndpoint = config?.api?.lookupEndpoints?.users ?? '/users';
  const { data: usersList = [] } = useFlexibleList(usersEndpoint);
  const userOptions = (usersList || []).map((u) => ({ value: u.id, label: u.userDisplayName || u.userEmail || String(u.id) }));

  const { data: existingAudit, isLoading: auditLoading } = useEntity(
    '/external-audit',
    recordId,
  );

  const isViewMode =
    (selectedRecord && !isEditMode) || typeof setFormData !== 'function';

  useEffect(() => {
    isInitializedRef.current = false;
  }, [recordId]);

  useEffect(() => {
    if (isInitializedRef.current) return;

    if (existingAudit?.findings && Array.isArray(existingAudit.findings) && existingAudit.findings.length > 0) {
      setLocalRows(flattenFindingsForForm(existingAudit.findings));
      isInitializedRef.current = true;
      return;
    }

    if (selectedRecord?.findings && Array.isArray(selectedRecord.findings) && selectedRecord.findings.length > 0) {
      setLocalRows(flattenFindingsForForm(selectedRecord.findings));
      isInitializedRef.current = true;
      return;
    }

    if (formData?.findings && Array.isArray(formData.findings) && formData.findings.length > 0) {
      setLocalRows(formData.findings.map((r) => ({ ...r })));
      isInitializedRef.current = true;
      return;
    }

    // Wait until single-record fetch finishes before concluding there are no findings
    if (recordId && auditLoading) return;

    setLocalRows([]);
    isInitializedRef.current = true;
  }, [recordId, existingAudit, selectedRecord, formData?.findings, auditLoading]);

  useEffect(() => {
    localRowsRef.current = localRows;
  }, [localRows]);

  useEffect(() => {
    if (!isInitializedRef.current) return;
    if (typeof setFormData !== 'function') return;
    setFormData((prev) => ({
      ...prev,
      findings: localRows.map((r) => ({ ...r })),
    }));
  }, [localRows, setFormData]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.__getExternalAuditFindings = () => ({
      localRows: localRowsRef.current,
      payload: findingsToPayload(localRowsRef.current),
    });
  });

  const addRow = () => {
    const newIndex = localRows.length;
    setLocalRows((prev) => [
      ...prev,
      {
        id: `temp-${Date.now()}`,
        code: '',
        description: '',
        criticalityId: null,
        gxpReference: '',
        rootCauseCategoryId: null,
        rootCauseDetails: '',
        actionDescription: '',
        responsiblePersons: '',
        responsibleUserIds: [],
        dueDate: null,
        completionDate: null,
        extensionRequested: false,
        extensionReason: '',
        newDueDate: null,
        newCompletionDate: null,
        statusId: null,
      },
    ]);
    setExpandedRowIndex((prev) => new Set([...prev, newIndex]));
    setIsExpanded(true);
  };

  const toggleRowExpanded = (index) => {
    setExpandedRowIndex((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const removeRow = (index) => {
    setLocalRows((prev) => prev.filter((_, i) => i !== index));
    setExpandedRowIndex((prev) => new Set(
      [...prev].filter((i) => i !== index).map((i) => (i > index ? i - 1 : i))
    ));
  };

  const updateRow = (index, field, value) => {
    setLocalRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const formatDate = (d) => {
    if (!d) return '';
    try {
      return new Date(d).toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  return (
    <div className={cn('border rounded-lg', theme.border.light, theme.colors.background)}>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          'w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors',
          theme.text.primary
        )}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">📄</span>
          <span className="font-semibold">Findings &amp; CAPA Actions</span>
          {localRows.length > 0 && (
            <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', theme.colors.primary, theme.text.inverse)}>
              {localRows.length}
            </span>
          )}
        </div>
        {isExpanded ? <ChevronUpIcon className="w-5 h-5" /> : <ChevronDownIcon className="w-5 h-5" />}
      </button>

      {isExpanded && (
        <div className={cn('p-4 border-t', theme.border.light)}>
          {localRows.length === 0 ? (
            <div className={cn('text-center py-8', theme.text.secondary)}>
              <p className="mb-4">No findings yet.</p>
              {!isViewMode && (
                <Button type="button" variant="outline" size="sm" onClick={addRow}>
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add first row
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {localRows.map((row, index) => {
                  const isRowExpanded = isViewMode || expandedRowIndex.has(index);
                  const completionDateStr = row.completionDate ? new Date(row.completionDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
                  const summary = [row.code, completionDateStr, row.status?.name].filter(Boolean).join(' · ') || `Row #${index + 1}`;
                  return (
                  <div
                    key={row.id ?? index}
                    className={cn(
                      'rounded-lg border overflow-hidden',
                      theme.border.subtle,
                      theme.colors.backgroundAlt
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => !isViewMode && toggleRowExpanded(index)}
                      className={cn(
                        'w-full flex items-center justify-between p-3 text-left transition-colors',
                        !isViewMode && 'hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer',
                        isViewMode && 'cursor-default',
                        theme.text.primary
                      )}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {!isViewMode && (isRowExpanded ? (
                          <ChevronUpIcon className="w-5 h-5 flex-shrink-0 text-gray-500" />
                        ) : (
                          <ChevronRightIcon className="w-5 h-5 flex-shrink-0 text-gray-500" />
                        ))}
                        <span className={cn('text-sm font-medium truncate', theme.text.secondary)}>
                          {isRowExpanded ? `Row #${index + 1}` : summary}
                        </span>
                      </div>
                      {!isViewMode && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); removeRow(index); }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900 flex-shrink-0"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </Button>
                      )}
                    </button>
                    {isRowExpanded && (
                    <div className="p-4 pt-0 space-y-3 border-t" style={{ borderColor: theme.border?.subtle }}>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-3">
                      <div className="lg:col-span-1">
                        <Label>Code</Label>
                        {isViewMode ? (
                          <div className={cn('text-sm p-2 rounded', theme.colors.background, theme.text.primary)}>{row.code || '—'}</div>
                        ) : (
                          <Input
                            value={row.code || ''}
                            onChange={(e) => updateRow(index, 'code', e.target.value)}
                            placeholder="e.g. 3.1"
                          />
                        )}
                      </div>
                      <div className="lg:col-span-9">
                        <Label>Description</Label>
                        {isViewMode ? (
                          <div className={cn('text-sm p-2 rounded', theme.colors.background, theme.text.primary)}>{row.description || '—'}</div>
                        ) : (
                          <Textarea
                            value={row.description || ''}
                            onChange={(e) => updateRow(index, 'description', e.target.value)}
                            placeholder="Finding description"
                            rows={3}
                          />
                        )}
                      </div>
                      <div className="lg:col-span-2 space-y-3">
                        <div>
                          <Label>Criticality</Label>
                          {isViewMode ? (
                            <div className={cn('text-sm p-2 rounded', theme.colors.background, theme.text.primary)}>
                              {row.criticality?.name ?? '—'}
                            </div>
                          ) : (
                            <Select
                              value={row.criticalityId ?? ''}
                              onChange={(e) => updateRow(index, 'criticalityId', e.target.value ? Number(e.target.value) : null)}
                              placeholder="Select"
                            >
                              {(config?.lookupData?.externalAuditCriticality ?? []).map((c) => (
                                <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                              ))}
                            </Select>
                          )}
                        </div>
                        <div>
                          <Label>Root cause</Label>
                          {isViewMode ? (
                            <div className={cn('text-sm p-2 rounded', theme.colors.background, theme.text.primary)}>
                              {row.rootCauseCategory?.name ?? '—'}
                            </div>
                          ) : (
                            <Select
                              value={row.rootCauseCategoryId ?? ''}
                              onChange={(e) => updateRow(index, 'rootCauseCategoryId', e.target.value ? Number(e.target.value) : null)}
                              placeholder="Select"
                            >
                              {(config?.lookupData?.externalAuditRootCauseCategory ?? []).map((r) => (
                                <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>
                              ))}
                            </Select>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-3 border-t pt-3" style={{ borderColor: theme.border?.subtle }}>
                      <div className="lg:col-span-6">
                        <Label>Action description</Label>
                        {isViewMode ? (
                          <div className={cn('text-sm p-2 rounded', theme.colors.background, theme.text.primary)}>{row.actionDescription || '—'}</div>
                        ) : (
                          <Textarea
                            value={row.actionDescription || ''}
                            onChange={(e) => updateRow(index, 'actionDescription', e.target.value)}
                            placeholder="CAPA action"
                            rows={3}
                          />
                        )}
                      </div>
                      <div className="lg:col-span-2 space-y-3">
                        <div>
                          <Label>Responsible</Label>
                          {isViewMode ? (
                            <div className={cn('text-sm p-2 rounded', theme.colors.background, theme.text.primary)}>
                              {(Array.isArray(row.responsibleUserIds) && row.responsibleUserIds.length > 0
                                ? row.responsibleUserIds
                                    .map((uid) => userOptions.find((o) => Number(o.value) === Number(uid))?.label)
                                    .filter(Boolean)
                                    .join(', ')
                                : null) || row.responsiblePersons || '—'}
                            </div>
                          ) : (
                            <MultiSelect
                              options={userOptions}
                              value={Array.isArray(row.responsibleUserIds) ? row.responsibleUserIds : []}
                              onChange={(selected) => updateRow(index, 'responsibleUserIds', selected)}
                              placeholder="Select users..."
                              maxDisplay={2}
                            />
                          )}
                        </div>
                        <div className="mt-4">
                          <Label>Due date</Label>
                          {isViewMode ? (
                            <div className={cn('text-sm p-2 rounded', theme.colors.background, theme.text.primary)}>{row.dueDate ? new Date(row.dueDate).toLocaleDateString() : '—'}</div>
                          ) : (
                            <DateInput
                              value={formatDate(row.dueDate)}
                              onChange={(e) => updateRow(index, 'dueDate', e.target.value ? new Date(e.target.value) : null)}
                              showFormatHint={false}
                            />
                          )}
                        </div>
                      </div>
                      <div className="lg:col-span-2 space-y-3">
                        <div>
                          <Label>Completion date</Label>
                          {isViewMode ? (
                            <div className={cn('text-sm p-2 rounded', theme.colors.background, theme.text.primary)}>{row.completionDate ? new Date(row.completionDate).toLocaleDateString() : '—'}</div>
                          ) : (
                            <DateInput
                              value={formatDate(row.completionDate)}
                              onChange={(e) => updateRow(index, 'completionDate', e.target.value ? new Date(e.target.value) : null)}
                              showFormatHint={false}
                            />
                          )}
                        </div>
                        <div className="mt-4">
                          <Label>New completion date</Label>
                          {isViewMode ? (
                            <div className={cn('text-sm p-2 rounded', theme.colors.background, theme.text.primary)}>{row.newCompletionDate ? new Date(row.newCompletionDate).toLocaleDateString() : '—'}</div>
                          ) : (
                            <DateInput
                              value={formatDate(row.newCompletionDate)}
                              onChange={(e) => updateRow(index, 'newCompletionDate', e.target.value ? new Date(e.target.value) : null)}
                              showFormatHint={false}
                            />
                          )}
                        </div>
                      </div>
                      <div className="lg:col-span-2">
                        <Label>Action status</Label>
                        {isViewMode ? (
                          <div className={cn('text-sm p-2 rounded', theme.colors.background, theme.text.primary)}>{row.status?.name ?? '—'}</div>
                        ) : (
                          <Select
                            value={row.statusId ?? ''}
                            onChange={(e) => updateRow(index, 'statusId', e.target.value ? Number(e.target.value) : null)}
                            placeholder="Select"
                          >
                            {(config?.lookupData?.capaActionStatus ?? []).map((s) => (
                              <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                            ))}
                          </Select>
                        )}
                      </div>
                    </div>
                    </div>
                    )}
                  </div>
                );
                })}
              </div>
              {!isViewMode && (
                <Button type="button" variant="outline" size="sm" onClick={addRow} className="mt-4 w-full">
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add row
                </Button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ExternalAuditFindingsTable;
